// AcceptInvitePage — public /accept-invite/:token route.
// Verifies the invite token, prompts the user for a password, sets the
// password, then redirects to the login screen with the email pre-filled.

import { ReactNode, useEffect, useState } from "react";
import { T } from "../theme";
import { Btn, Card, Inp } from "./primitives";

type VerifyState =
  | { kind: "loading" }
  | { kind: "valid"; email: string; name: string; role: string; expires_at: string }
  | { kind: "invalid"; reason: string };

export const AcceptInvitePage = ({ token }: { token: string }) => {
  const [verify, setVerify] = useState<VerifyState>({ kind: "loading" });
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/invites/verify/${encodeURIComponent(token)}`);
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.valid) {
          setVerify({
            kind: "valid",
            email: data.email,
            name: data.name,
            role: data.role,
            expires_at: data.expires_at,
          });
        } else {
          setVerify({ kind: "invalid", reason: data.reason || "not_found" });
        }
      } catch {
        if (!cancelled) setVerify({ kind: "invalid", reason: "network_error" });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    setErrors([]);
    const localErrors: string[] = [];
    if (!password) localErrors.push("Password is required.");
    if (password && password !== confirmPw) localErrors.push("Passwords do not match.");
    if (localErrors.length) { setErrors(localErrors); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`/api/invites/accept/${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setSuccess(true);
        setTimeout(() => {
          const email = verify.kind === "valid" ? verify.email : "";
          window.location.href = `/?invite_accepted=1&email=${encodeURIComponent(email)}`;
        }, 1500);
        return;
      }
      if (data.error === "password_policy_violation" && Array.isArray(data.errors)) {
        setErrors(data.errors);
      } else if (data.error === "expired") {
        setErrors(["This invite link has expired. Ask your administrator to send a new welcome email."]);
      } else if (data.error === "already_used") {
        setErrors(["This invite link has already been used. If you did not set your password, ask your administrator to send a new welcome email."]);
      } else if (data.error === "not_found") {
        setErrors(["This invite link is not valid. Double-check the URL in your email, or ask your administrator to resend."]);
      } else if (data.error) {
        setErrors([String(data.error)]);
      } else {
        setErrors(["Password could not be set. Please try again."]);
      }
    } catch {
      setErrors(["Connection error. Please try again."]);
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (inner: ReactNode) => (
    <div
      data-deploy-marker="wave2_5-accept-invite"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#F5F7FA",
        fontFamily: "'Outfit', 'Segoe UI', sans-serif",
      }}
    >
      <div style={{ textAlign: "center", width: 420, maxWidth: "95%" }}>
        <div style={{ marginBottom: 28 }}>
          <img src="/logo.png" alt="KontractAI" style={{ height: 48, marginBottom: 12 }} />
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".08em", color: T.txD, textTransform: "uppercase" }}>
            Contract Lifecycle Management
          </div>
        </div>
        <Card style={{ textAlign: "left", boxShadow: "0 2px 12px rgba(0,0,0,.06)", padding: 28 }}>
          {inner}
        </Card>
      </div>
    </div>
  );

  if (verify.kind === "loading") {
    return shell(<div style={{ fontSize: 13, color: T.txM, textAlign: "center", padding: 20 }}>Verifying your invite...</div>);
  }

  if (verify.kind === "invalid") {
    const messages: Record<string, string> = {
      not_found: "This invite link is not valid. Double-check the URL in your email, or ask your administrator to resend.",
      expired: "This invite link has expired. Ask your administrator to send a new welcome email.",
      already_used: "This invite link has already been used. If you did not set your password, ask your administrator to send a new welcome email.",
      network_error: "We could not reach the server. Please try again.",
    };
    return shell(
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.tx, marginBottom: 8, textAlign: "center" }}>Invite unavailable</div>
        <div style={{ fontSize: 13, color: T.txM, marginBottom: 20, textAlign: "center" }}>
          {messages[verify.reason] || messages.not_found}
        </div>
        <Btn v="primary" full onClick={() => { window.location.href = "/"; }}>
          Go to sign-in
        </Btn>
      </div>
    );
  }

  if (success) {
    return shell(
      <div style={{ textAlign: "center", padding: "12px 0" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.grn, marginBottom: 8 }}>Password set</div>
        <div style={{ fontSize: 13, color: T.txM, marginBottom: 4 }}>Redirecting you to sign in...</div>
      </div>
    );
  }

  return shell(
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.tx, marginBottom: 6, textAlign: "center" }}>
        Welcome, {verify.name}
      </div>
      <div style={{ fontSize: 12, color: T.txM, marginBottom: 16, textAlign: "center" }}>
        Choose a password for <strong>{verify.email}</strong> to complete your account setup.
      </div>

      <Inp
        label="New Password"
        value={password}
        onChange={setPassword}
        type="password"
        placeholder="Enter a new password"
      />
      <Inp
        label="Confirm Password"
        value={confirmPw}
        onChange={setConfirmPw}
        type="password"
        placeholder="Re-enter the password"
      />

      {errors.length > 0 && (
        <div style={{ fontSize: 11, color: T.red, backgroundColor: T.redS, padding: "8px 10px", borderRadius: 4, marginBottom: 12 }}>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <Btn
        v="primary"
        sz="lg"
        full
        onClick={() => { submit(); }}
        disabled={submitting || !password || !confirmPw}
      >
        {submitting ? "Setting password..." : "Set password and continue"}
      </Btn>

      <div style={{ fontSize: 11, color: T.txD, marginTop: 14, textAlign: "center" }}>
        After you set your password, you will sign in. If two-factor
        authentication is required for your account, you will be walked
        through setup on first sign-in.
      </div>
    </div>
  );
};
